import React, { FC } from 'react'
import { Route, Switch, useHistory } from 'react-router-dom'
import { Toast, useToastState } from 'transfix-ui/components/Toast'
import { Box } from 'transfix-ui/core'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { ResponsiveLayout } from 'apps/fleet-planner/components/layout/ResponsiveLayout'
import { NavListMobile } from 'apps/fleet-planner/components/user/NavListMobile'
import CarrierProfilesPage from '../carrier-profile/CarrierProfilesPage'
import { AccountDeletionWarningPage } from './AccountDeletionWarningPage'
import { DateAndTimeMobilePage } from './DateAndTimeMobilePage'
import { ELDIntegrationsPage } from './ELDIntegrationsPage'
import { EmailPreferencesMobilePage } from './EmailPreferencesMobilePage'
import { NameMobilePage } from './NameMobilePage'
import { PasswordMobilePage } from './PasswordMobilePage'

const useUserSettingsMobileStyles = makeStyles({
  page: {
    height: '100%',
  },
  navContainer: {
    background: brandColors.coolGray1,
  },
  listWrapper: {
    height: '100%',
    background: brandColors.coolGray1,
  },
})

/**
 * Page for all user-toggled settings for Fleet Planner
 */
export const UserSettingsMobile: FC = () => {
  const classes = useUserSettingsMobileStyles()
  const isFmsLaneRecommendationsEnabled = useFeatureFlag('fmsLaneRecommendations')
  const isAccountDeletionEnabled = useFeatureFlag('fmsAccountDeletion')
  const isCarrierProfileEnabled = useFeatureFlag('motorCarrierProfileShowFmsTag')
  const history = useHistory()
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()

  const goToDefaultRouteHandler = () => history.push('/user/settings/general')

  return (
    <Box className={classes.page}>
      <Switch>
        <Route path='/user/settings/date-time' exact>
          <DateAndTimeMobilePage
            onGoToDefaultRoute={goToDefaultRouteHandler}
            onOpenToast={openToast}
          />
        </Route>

        <Route path='/user/settings/name' exact>
          <NameMobilePage onGoToDefaultRoute={goToDefaultRouteHandler} onOpenToast={openToast} />
        </Route>

        <Route path='/user/settings/change-password' exact>
          <PasswordMobilePage
            onGoToDefaultRoute={goToDefaultRouteHandler}
            onOpenToast={openToast}
          />
        </Route>

        <Route path='/user/settings/eld-integrations' exact>
          <ELDIntegrationsPage onGoToDefaultRoute={goToDefaultRouteHandler} />
        </Route>

        {isFmsLaneRecommendationsEnabled && (
          <Route path='/user/settings/email-preferences' exact>
            <EmailPreferencesMobilePage
              onGoToDefaultRoute={goToDefaultRouteHandler}
              onOpenToast={openToast}
            />
          </Route>
        )}

        {isCarrierProfileEnabled && (
          <Route path='/user/settings/carrier-profiles' exact>
            <CarrierProfilesPage />
          </Route>
        )}

        {isAccountDeletionEnabled && (
          <Route path='/user/settings/account-deletion-warning' exact>
            <AccountDeletionWarningPage onGoToDefaultRoute={goToDefaultRouteHandler} />
          </Route>
        )}

        {/* default route - navigation */}
        <Route>
          <ResponsiveLayout
            title='Settings'
            containerClassName={classes.navContainer}
            hidePaddingTop>
            {isCarrierProfileEnabled && (
              <Box className={classes.listWrapper}>
                <NavListMobile
                  items={[
                    { label: 'Name', path: '/user/settings/name' },
                    {
                      label: 'Date & Time',
                      path: '/user/settings/date-time',
                    },
                    { label: 'Change Password', path: '/user/settings/change-password' },
                  ]}
                />
                <NavListMobile
                  items={[{ label: 'Carrier Profiles', path: '/user/settings/carrier-profiles' }]}
                />
                <NavListMobile
                  items={[{ label: 'ELD Integrations', path: '/user/settings/eld-integrations' }]}
                />
                {isFmsLaneRecommendationsEnabled && (
                  <NavListMobile
                    items={[
                      { label: 'Email Preferences', path: '/user/settings/email-preferences' },
                    ]}
                  />
                )}
                {isAccountDeletionEnabled && (
                  <NavListMobile
                    useErrorColors
                    items={[
                      {
                        label: 'Delete Your Account',
                        path: '/user/settings/account-deletion-warning',
                      },
                    ]}
                  />
                )}
              </Box>
            )}
            {!isCarrierProfileEnabled && (
              <Box className={classes.listWrapper}>
                <NavListMobile
                  listTitle='General'
                  items={[
                    {
                      label: 'Date & Time',
                      path: '/user/settings/date-time',
                    },
                  ]}
                />

                <NavListMobile
                  listTitle='Profile'
                  items={[
                    { label: 'Name', path: '/user/settings/name' },
                    { label: 'Change Password', path: '/user/settings/change-password' },
                  ]}
                />

                <NavListMobile
                  listTitle='Integrations'
                  items={[{ label: 'ELD Integrations', path: '/user/settings/eld-integrations' }]}
                />

                {isFmsLaneRecommendationsEnabled && (
                  <NavListMobile
                    listTitle='Notifications'
                    items={[
                      { label: 'Email Preferences', path: '/user/settings/email-preferences' },
                    ]}
                  />
                )}

                {isAccountDeletionEnabled && (
                  <NavListMobile
                    useErrorColors
                    items={[
                      {
                        label: 'Delete Your Account',
                        path: '/user/settings/account-deletion-warning',
                      },
                    ]}
                  />
                )}
              </Box>
            )}
          </ResponsiveLayout>
        </Route>
      </Switch>

      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
    </Box>
  )
}
